import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { every, find, isArray, isEmpty, map } from 'lodash';

// Components
import PaperSelect from 'src/components/common/PaperSelect';

// Utilities
import { ReactNode, useMemo } from 'react';
import iconSearch from 'src/asset/icons/icon-search.svg';
import { useQueryJurisdictions } from 'src/services/queries';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { checkCountryHaveState, getCountryByCountryCode } from 'src/utils/helpers/stringHelpers';
import FlagCountry from 'src/components/common/FlagCountry';

interface IProps {
  required?: boolean;
  error?: boolean;
  id: string;
  countryCode?: string[];
  label?: ReactNode;
  disabled?: boolean;
}

interface IJurisdictionOption extends IJurisdictions {
  optionLabel: string;
}

const FormItemStatePeople = ({ required, id, error, countryCode, label, disabled = false }: IProps) => {
  const { data, isFetching } = useQueryJurisdictions(countryCode || []);
  const { data: listJurisdiction = [] } = data;
  const { values, setFieldValue, handleBlur } = useFormikContext<ExpectedAny>();
  const options: IJurisdictionOption[] = useMemo(() => {
    const copy = [...listJurisdiction];
    const mappingWithCountryState: IJurisdictionOption[] = map<IJurisdictions, IJurisdictionOption>(copy, (item) => {
      const isCountryHaveState = checkCountryHaveState(item?.country_code);
      if (isCountryHaveState) {
        const country = getCountryByCountryCode(item.country_code);
        return {
          ...item,
          ...{
            optionLabel: `${country.label} - ${item.jurisdiction}`,
          },
        } as IJurisdictionOption;
      }
      return {
        ...item,
        ...{
          optionLabel: item.jurisdiction,
        },
      };
    });
    return mappingWithCountryState.sort((a, b) => a.optionLabel.localeCompare(b.optionLabel));
  }, [listJurisdiction]);

  const getJurisdiction = (jurisdictionName: string) => {
    if (!jurisdictionName || isEmpty(options)) {
      return {
        country_code: '',
        jurisdiction: '',
        optionLabel: '',
      };
    }
    const jurisdictionItem = find(options, (o) => o.jurisdiction === jurisdictionName);
    if (!jurisdictionItem) {
      return {
        country_code: '',
        jurisdiction: '',
        optionLabel: '',
      };
    }
    return jurisdictionItem;
  };

  const getValue = () => {
    // if value is a list of Jurisdiction => get list Jurisdiction from list listJurisdiction
    if (typeof values[id] === 'string') {
      return getJurisdiction(values[id]);
    }
    return values[id];
  };

  return (
    <Autocomplete
      value={getValue()}
      loading={isFetching}
      fullWidth
      size="small"
      onBlur={handleBlur}
      getOptionKey={(option) => option.optionLabel}
      options={getArrayDefault(options)}
      popupIcon={<Box component={'img'} src={iconSearch} />}
      PaperComponent={PaperSelect}
      disabled={disabled}
      onChange={(_, value) => {
        setFieldValue(id, value?.jurisdiction);
      }}
      getOptionLabel={(option: IJurisdictionOption) => option.optionLabel}
      renderOption={(props, option) => {
        return (
          <Box component="li" display="flex" flexDirection="row" {...props}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Box>
                <FlagCountry width={20} code={option?.country_code || ''} />
              </Box>
              <Typography variant="body2">{option?.optionLabel}</Typography>
            </Stack>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          id={id}
          name={id}
          placeholder="Search"
          InputLabelProps={{ required: required }}
          label={<span>{label ?? 'Jurisdictions'}</span>}
        />
      )}
    />
  );
};

export default FormItemStatePeople;
